/**
 * What the Budgets screen shows, apart from anything that draws it.
 *
 * Every function here is pure and exported, because a budget is the one screen
 * where a wrong answer is indistinguishable from a right one: 243.7 kg and
 * 141.85 kg are both plausible masses for the same vehicle, and only one of them
 * is what the model says. Three traps in particular, all of which this module
 * exists to hold shut:
 *
 *   - **The roots overlap.** `GET /api/design` answers every UNCONTAINED usage
 *     as a root, and the example program has six: `vehicle`, and then
 *     `guidance`, `structure` and `flightSoftware` - which are standalone usages
 *     of the very definitions already sitting INSIDE `vehicle` - plus two
 *     stakeholders. Summing the roots double counts most of the vehicle. So the
 *     page reports ONE root at a time and says the others exist; it never adds
 *     them up.
 *   - **`own` is null on every design row.** The rows are usages and a quantity
 *     is stated on the DEFINITION, so the tree cannot tell you what a part
 *     weighs by itself. The children therefore do not sum to their parent's
 *     total, and the difference is not an error - it is the definition's own
 *     stated value. It is recovered as a residual and NAMED, because an
 *     unexplained gap between a headline and its breakdown is how a reader
 *     decides the screen is broken.
 *   - **Four states, not one number.** A figure, a bound (`bound`, a floor or a
 *     ceiling), a partial (`is_partial`, with `unstated` naming who), and
 *     unknowable (`total` is null). Unset is not zero, and a floor is not a
 *     figure. See `PartTotal`'s doc comment; nothing here is allowed to collapse
 *     the two, because a bounded total that is not partial and a partial that is
 *     exact are both real and mean different things. Which DIRECTION a bound
 *     points is the payload's answer, not this module's: a `min` aggregation
 *     yields a ceiling, and drawing it as a floor turns a shortfall into a
 *     margin.
 *
 * WHICH quantities exist is read from `attributes` and never from a list here.
 * An attribute rolls up because it carries `@EchelonSysML::RollsUp` in the
 * library, so a program adding delta-V or a timing budget must appear on this
 * screen with nothing on this side being edited.
 */

import type {
  Aggregation,
  Bound,
  BudgetCheck,
  ComplianceStatus,
  DesignRow,
  PartTotal,
  RollingAttribute,
} from '../../schema'

/**
 * `GET /api/design` in full.
 *
 * `api.design()` keeps answering only `items` because the diagram and the parts
 * tree want exactly that; the budget needs the other two keys, and `attributes`
 * in particular, since it is what says how many charts there are.
 */
export interface DesignBudget {
  attributes: RollingAttribute[]
  unstorable: string[]
  items: DesignRow[]
}

/**
 * Floating point noise, removed before anything is compared or shown.
 *
 * The residual is `total - sum(children)`, and a subtraction of decimals can
 * leave an artefact that would reach the screen verbatim: `1 - (0.7 + 0.1)` is
 * `0.19999999999999996`. Rounding at six decimals is far finer than any quantity
 * a model states and coarse enough to erase it.
 *
 * CORRECTED 2026-08-18: this comment previously claimed
 * `141.85 - (16.8 + 85.05)` yields `39.99999999999999`. It does not - that one is
 * exactly `40.0` in IEEE 754, in every order of operations. The guard is right;
 * the example was wrong, and it was wrong in a way that mattered: a test written
 * from it passed with this function reduced to the identity, and only mutating
 * the code exposed that. The example above is verified.
 */
export function tidy(value: number): number {
  return Math.round(value * 1e6) / 1e6
}

/**
 * A quantity's unit, as a person writes it.
 *
 * The model stores `SI::kg`, which is right for a database and wrong on a tile.
 * Split on the last `::` rather than stripping a known prefix, so a unit from a
 * package this code has never heard of still reads correctly, and drop the
 * quotes the model needs around a symbol that is not an identifier -
 * `SI::'kg⋅m²'` is one unit, not a quoted string.
 *
 * `_abs` GOES TOO, and that one is a judgement rather than a formatting rule.
 * The library distinguishes `°C_abs`, a reading on the absolute scale, from
 * `°C`, an interval of five degrees - a real distinction that decides whether
 * an equation is right. It is NOT a distinction anybody needs when reading a
 * temperature limit: a survival range of -40 to 85 is obviously readings, and
 * `°C_abs` in that sentence reads as a typo. The stored value keeps the suffix
 * and the model keeps the distinction; only the symbol on screen loses it.
 *
 * The cost, stated because it is real: a model that stated both against one
 * part would show them identically. Nothing does today, and the two would be
 * different attributes with different labels beside them if it did.
 *
 * @param unit the unit as the API answers it, e.g. `SI::kg`
 */
export function unitLabel(unit: string): string {
  const symbol = unitSymbol(unit)
  return symbol.endsWith('_abs') ? symbol.slice(0, -'_abs'.length) : symbol
}

/**
 * A unit's IDENTITY - the last segment, unquoted - as the server compares it.
 *
 * `rollup.same_unit` compares on exactly this: `SI::kg` and `kg` are the same
 * statement, and so are `SI::'°C_abs'` and `SI::°C_abs`, because a model may
 * write either and the loader stores what it read.
 *
 * DISTINCT FROM `unitLabel`, and the difference is the whole reason there are
 * two. `unitLabel` also drops `_abs` because nobody wants to read it on a
 * temperature range - but `°C_abs` is a reading on the absolute scale and `°C`
 * is an interval of five degrees, genuinely different quantities. Comparing
 * with the display form would call them equal and quietly accept one where the
 * other belongs.
 */
export function unitSymbol(unit: string): string {
  const parts = String(unit ?? '').split('::')
  return (parts[parts.length - 1] || '').replace(/'/g, '')
}

/**
 * Whether two unit references name the same unit, the way the server decides.
 *
 * @param left one unit reference
 * @param right the other
 */
export function sameUnit(left: string, right: string): boolean {
  return unitSymbol(left) === unitSymbol(right)
}

/**
 * A number, without the noise and without a false precision.
 *
 * Grouped at the thousands because a power budget in watts reaches five digits,
 * and six decimals kept because `tidy` is the only rounding this module is
 * entitled to do - a mass of 0.05 kg is a real fastener, not a rounding error.
 */
export function formatNumber(value: number): string {
  return tidy(value).toLocaleString('en-US', { maximumFractionDigits: 6 })
}

/**
 * One rolled-up quantity as it must READ - all four states, never just a number.
 *
 * `≥` and `≤` are not decoration. `bound` says the number is short of the truth
 * or over it: nobody counts fasteners, and `85.05 kg` of structure is a claim
 * the model cannot support while `≥ 85.05 kg` is exactly what it says. A null
 * total renders as `not stated`, never `0` - the two are the same width on
 * screen and opposite in meaning.
 *
 * The DIRECTION comes from the payload and is never assumed here. A `min` - a
 * weakest link, not a total - is too HIGH when something below it is unstated,
 * so `≥` on that number would read a shortfall as a margin.
 *
 * @param total the quantity, or null/undefined when the row carries none
 */
export function formatTotal(total: PartTotal | null | undefined): string {
  if (!total || total.total === null || total.total === undefined) return 'not stated'
  const figure = `${formatNumber(total.total)} ${unitLabel(total.unit)}`.trim()
  if (total.bound === 'at_least') return `≥ ${figure}`
  if (total.bound === 'at_most') return `≤ ${figure}`
  return figure
}

/**
 * How a slice may be DRAWN, which is a coarser question than what it means.
 *
 * Three marks for four states, deliberately: a solid bar is reserved for a
 * figure, `hatched` covers both caveats because a bar cannot be half solid, and
 * `none` is not a zero-length bar. What the caveat actually IS stays on the
 * slice and in the text beside it - collapsing "a floor" and "something below is
 * unstated" into one word is the mistake the payload's two separate flags exist
 * to prevent.
 */
export type SliceMark = 'solid' | 'hatched' | 'none'

/**
 * @param slice one row of a breakdown
 * @returns the mark to draw; never `solid` for a total carrying a caveat
 */
export function sliceMark(slice: BudgetSlice): SliceMark {
  if (slice.total === null) return 'none'
  if (slice.isPartial || slice.bound !== 'exact') return 'hatched'
  return 'solid'
}

/**
 * One contributor to a budget - a subsystem, or the root's own undecomposed value.
 *
 * The flags are carried rather than reduced, so the bar can be hatched while the
 * label still says which of the two caveats applies.
 */
export interface BudgetSlice {
  /** Unique within one breakdown; the design row's index, or `own`. */
  key: string
  label: string
  /** The part's ID, shown in monospace. Empty for the residual, which has none. */
  id: string
  total: number | null
  unit: string
  isPartial: boolean
  /** Which function combined the subtree; a 'min' is a weakest link, not a sum. */
  how: Aggregation
  bound: Bound
  unstated: string[]
  problems: string[]
  /**
   * The root's own stated value, recovered as `total - Σ children`.
   *
   * Marked because it is the one slice with no design row behind it: it is
   * arithmetic, not a part, and a reader entitled to click through to every
   * other slice must not be offered a link to this one.
   */
  isResidual: boolean
}

/** One quantity's budget for one root. */
export interface Breakdown {
  attribute: RollingAttribute
  /** The root's own rolled-up total - the headline this breaks down. */
  total: PartTotal | null
  slices: BudgetSlice[]
  /** Every part named as stating nothing, across the root and its children. */
  unstated: string[]
  /** Every value that could not be summed, and why. */
  problems: string[]
}

/** A top-level part of the design, with where it sits in the flat rows. */
export interface DesignRoot {
  index: number
  row: DesignRow
  /** How many rows sit under it - what makes one root the system and the rest usages. */
  descendants: number
}

/**
 * How many rows sit below `index`, by depth.
 *
 * The rows are depth-first, so a subtree is the run of rows deeper than its
 * root, and it ends at the first row that is not. Counting by scanning to the
 * next row of equal-or-shallower depth is the whole algorithm; the off-by-one
 * version - stopping at `depth < root.depth` - swallows every sibling that
 * follows and makes the first root look like the entire model.
 */
export function descendantCount(items: DesignRow[], index: number): number {
  const depth = items[index]?.depth ?? 0
  let count = 0
  for (let at = index + 1; at < items.length; at += 1) {
    if (items[at].depth <= depth) break
    count += 1
  }
  return count
}

/**
 * The top-level parts, in the order the endpoint answers them.
 *
 * Every uncontained usage is one, which is why there are six in a program with
 * one vehicle: a definition used inside the vehicle is ALSO declared standalone
 * in its own package, and stakeholders are parts too.
 */
export function designRoots(items: DesignRow[]): DesignRoot[] {
  const roots: DesignRoot[] = []
  items.forEach((row, index) => {
    if (row.depth === 0) {
      roots.push({ index, row, descendants: descendantCount(items, index) })
    }
  })
  return roots
}

/**
 * Which root to show first - the one that contains the most.
 *
 * Not "the first row" and emphatically not "all of them added up". The roots
 * OVERLAP: `guidance` is a standalone usage of the same definition the vehicle
 * contains, so a page that summed the roots would report the guidance section's
 * mass twice and a plausible number nobody could reconcile. Deepest subtree wins
 * because the system is the thing with the decomposition; ties keep declaration
 * order, so the choice is stable across reloads.
 *
 * @returns the chosen root, or null when the design has no rows at all
 */
export function defaultRoot(items: DesignRow[]): DesignRoot | null {
  // The MODEL's answer first. A part specializing `EchelonSystem` says it is
  // the thing system figures are reported for, which the descendant count only
  // ever guessed at - correctly on a model shaped like the example, silently
  // wrong on one that is not.
  const roots = designRoots(items)
  const systems = roots.filter((root) => root.row.is_system)
  if (systems.length > 0) return systems[0]

  // No part names itself as the system, so fall back to the guess. Kept rather
  // than refusing to render: a program that has not adopted the type still has
  // a design worth looking at, and the server raises a `system-type` finding so
  // the fallback is not silent.
  let best: DesignRoot | null = null
  for (const root of roots) {
    if (!best || root.descendants > best.descendants) best = root
  }
  return best
}

/**
 * Every root that names itself as a system, in declaration order.
 *
 * The plural is the point. Two is a variant study, or a flight article beside
 * its qualification article - legitimate, and it means nothing can call one of
 * them "the" system, so the page offers both rather than picking.
 */
export function systemRoots(items: DesignRow[]): DesignRoot[] {
  return designRoots(items).filter((root) => root.row.is_system)
}

/**
 * The rows one level under `index`.
 *
 * Immediate children only. Their totals already include everything beneath
 * them, so recursing here would count the whole design once per level.
 */
export function childrenOf(items: DesignRow[], index: number): { index: number; row: DesignRow }[] {
  const depth = items[index]?.depth ?? 0
  const children: { index: number; row: DesignRow }[] = []
  for (let at = index + 1; at < items.length; at += 1) {
    if (items[at].depth <= depth) break
    if (items[at].depth === depth + 1) children.push({ index: at, row: items[at] })
  }
  return children
}

/**
 * What a budget breaks down INTO - the subsystems under `index`, by type.
 *
 * The OUTERMOST ones, and that is the whole difficulty. Subsystems nest: an
 * organizational one has a lead, and de-facto ones sit inside it - Thermal
 * under Structures, EPS and Comms under Avionics. Gathering every subsystem
 * descendant would list Thermal beside Structures while Structures' total
 * already contains it, so the slices would sum past the total they claim to
 * break down. Taking a subsystem and not descending past it is what keeps the
 * rows disjoint.
 *
 * Falls back to the immediate children when the root has no subsystems below
 * it at all, because a program that has not adopted `EchelonSubsystem` still
 * has a decomposition worth charting - the same fallback `defaultRoot` makes,
 * for the same reason, and equally not silent: the server reports the type.
 *
 * @param items every design row, depth-first
 * @param index the root's position
 */
export function subsystemsOf(
  items: DesignRow[],
  index: number,
): { index: number; row: DesignRow }[] {
  const depth = items[index]?.depth ?? 0
  const found: { index: number; row: DesignRow }[] = []
  // Once a subsystem is taken at depth d, everything deeper than d belongs to
  // it and is skipped. Reset when the walk comes back out to d or shallower.
  let takenAt: number | null = null
  for (let at = index + 1; at < items.length; at += 1) {
    const row = items[at]
    if (row.depth <= depth) break
    if (takenAt !== null && row.depth > takenAt) continue
    takenAt = null
    if (row.is_subsystem) {
      found.push({ index: at, row })
      takenAt = row.depth
    }
  }
  return found.length > 0 ? found : childrenOf(items, index)
}

/** Anything smaller than this is float noise rather than a residual worth a row. */
const RESIDUAL_FLOOR = 1e-6

/** Deduped, order kept, so a report is stable and a name appears once. */
function unique(values: string[]): string[] {
  return values.filter((value, at) => values.indexOf(value) === at)
}

/**
 * One quantity's budget for one root: its subsystems, biggest first.
 *
 * The residual row is the load-bearing part. `total(def) = own(def) + Σ count(u)
 * × total(child)`, and the design rows carry no `own` - so `total` minus the
 * children IS the root's own stated value, exactly, whatever caveats the
 * children carry (a floor contributed its floor to both sides of the
 * subtraction, and a null contributed nothing to either). It is labelled as
 * undecomposed rather than dropped: 40 kg of harness and brackets missing from a
 * chart that claims to break down 141.85 kg is worse than an extra row.
 *
 * Sorted by magnitude, biggest first, because the question is where the mass is.
 * A row that states nothing sorts last and KEEPS ITS PLACE in the output - it is
 * a subsystem whose contribution is unknown, which is a different fact from a
 * subsystem contributing nothing, and dropping it would make the chart agree
 * with a total it does not explain.
 *
 * @param items every design row, as the endpoint answers them
 * @param index the root's position in those rows
 * @param attribute which quantity, read from the payload's `attributes`
 */
export function breakdown(
  items: DesignRow[],
  index: number,
  attribute: RollingAttribute,
): Breakdown {
  const root = items[index]
  const total = root?.totals?.[attribute.name] ?? null

  const slices: BudgetSlice[] = subsystemsOf(items, index).map((child) => {
    const quantity = child.row.totals?.[attribute.name]
    return {
      key: String(child.index),
      label: child.row.name,
      id: child.row.id,
      total: quantity?.total ?? null,
      unit: quantity?.unit ?? attribute.unit,
      isPartial: quantity?.is_partial ?? false,
      how: quantity?.how ?? 'sum',
      bound: quantity?.bound ?? 'exact',
      unstated: quantity?.unstated ?? [],
      problems: quantity?.problems ?? [],
      isResidual: false,
    }
  })

  // A RESIDUAL IS ARITHMETIC THAT ONLY A SUM SUPPORTS. `total - Σ children`
  // recovers the root's own value because the children were added to reach the
  // total; a `min` never added anything, so the same subtraction yields a
  // number with no meaning - and it would be drawn as a slice of a chart whose
  // slices do not add up in the first place. Omitted rather than approximated.
  if (total && total.total !== null && total.how === 'sum') {
    const accounted = slices.reduce((sum, slice) => sum + (slice.total ?? 0), 0)
    const residual = tidy(total.total - accounted)
    if (residual > RESIDUAL_FLOOR) {
      slices.push({
        key: 'own',
        label: `${root.name} itself, not decomposed`,
        id: '',
        total: residual,
        unit: total.unit,
        // A plain figure: the subtraction cancels every caveat the children
        // carry, so inheriting the parent's flags here would invent doubt.
        isPartial: false,
        how: 'sum',
        bound: 'exact',
        unstated: [],
        problems: [],
        isResidual: true,
      })
    }
  }

  // Biggest first, nulls last, declaration order kept within a tie. `sort` is
  // stable in every engine this runs on, so the comparator only has to be
  // total-ordered on the values it can compare.
  slices.sort((left, right) => {
    if (left.total === null && right.total === null) return 0
    if (left.total === null) return 1
    if (right.total === null) return -1
    return right.total - left.total
  })

  return {
    attribute,
    total,
    slices,
    unstated: unique([...(total?.unstated ?? []), ...slices.flatMap((slice) => slice.unstated)]),
    problems: unique([...(total?.problems ?? []), ...slices.flatMap((slice) => slice.problems)]),
  }
}

/**
 * Every quantity's breakdown for one root, in the order the model declares them.
 *
 * Driven by `attributes` and nothing else, so N quantities produce N charts. A
 * hard-coded three would have been shorter today and wrong the first time
 * somebody marked a fourth attribute `@RollsUp`.
 */
export function breakdowns(
  budget: DesignBudget,
  index: number,
): Breakdown[] {
  return budget.attributes.map((attribute) => breakdown(budget.items, index, attribute))
}


/* --------------------------------------------------------------------------
 * Budgets: what a part is ALLOWED, beside what it has.
 *
 * Everything below reports the server's verdict. Nothing here compares a total
 * to a budget, and nothing here computes a margin - not because it would be
 * hard, but because the arithmetic is the easy half and knowing when it is
 * MEANINGLESS is the hard half, and that answer is already in the payload.
 * ----------------------------------------------------------------------- */

/**
 * One budget as it must READ: the words, the strength of the claim, and a tone.
 *
 * `tone` maps to MUI severities, and the mapping is the part worth checking.
 * There are four verdicts and three tones, because `indeterminate` and
 * `not_comparable` share one: both mean NOT SETTLED, and the only tone that
 * would be wrong for either is the one that reads as a pass.
 */
export interface BudgetReading {
  status: ComplianceStatus
  /** A verdict in words - what a person reads first. */
  label: string
  /** MUI severity. `success` is reserved for a PROVEN pass. */
  tone: 'error' | 'success' | 'warning' | 'info'
  /** The budget, formatted with its unit, or 'not stated'. */
  budget: string
  /**
   * The margin, formatted and signed, or the reason there is not one.
   *
   * `no margin` rather than `0` or `-` on an unsettled answer: a dash reads as
   * "not shown" and invites someone to work it out themselves, which is the
   * exact subtraction this refuses to do.
   */
  margin: string
  /** True where a margin genuinely exists; false where none is possible. */
  hasMargin: boolean
  /** The server's own words, and the only thing distinguishing two refusals. */
  note: string
}

/**
 * How a verdict reads, and it is a table rather than a computation on purpose.
 *
 * THE ONE THAT MATTERS IS `indeterminate`. It is a total UNDER its budget, so
 * every instinct - and every naive `total <= budget` - calls it a pass. It is
 * not one: the total is a floor, the parts that state nothing could take it
 * over, and colouring it green hands somebody headroom the model never claimed.
 * It is `warning` here, and it is the reason this mapping is written out and
 * tested rather than inferred from a comparison.
 */
const READINGS: Record<
  ComplianceStatus,
  { label: string; tone: BudgetReading['tone'] }
> = {
  // PROVEN over. A floor above the ceiling is still above it.
  over: { label: 'Over budget', tone: 'error' },
  // PROVEN within - the only green on this screen, and it needs an EXACT
  // total to earn it.
  within: { label: 'Within budget', tone: 'success' },
  // Under the budget and NOT within it. See above.
  indeterminate: { label: 'Not settled', tone: 'warning' },
  // Nothing was compared at all; `note` says which refusal it was.
  not_comparable: { label: 'Not compared', tone: 'info' },
  // Never sent - the key is absent instead - and never rendered as a pass.
  unbudgeted: { label: 'No budget', tone: 'info' },
}

/**
 * A signed figure, with its sign always shown.
 *
 * `+3.2 kg` and `-5.05 kg` are the two answers a margin can be, and an unsigned
 * `5.05` beside the word "margin" reads as headroom whichever way it went.
 */
export function formatMargin(value: number, unit: string): string {
  const sign = value < 0 ? '-' : '+'
  return `${sign}${formatNumber(Math.abs(value))} ${unitLabel(unit)}`.trim()
}

/**
 * One budget check, ready to render.
 *
 * @param check the row's entry from `budgets`, or null/undefined where the part
 *   has none - which is `unbudgeted`, and is reported as absence by the caller
 *   rather than as a verdict here
 */
export function readBudget(check: BudgetCheck | null | undefined): BudgetReading | null {
  if (!check) return null
  const reading = READINGS[check.status] ?? READINGS.not_comparable

  // A MARGIN EXISTS ONLY WHERE THE SERVER SENT ONE. Not "where the numbers are
  // present": both numbers are present on an `indeterminate` and subtracting
  // them is precisely the mistake. The null IS the answer.
  const hasMargin = check.margin !== null && check.margin !== undefined
  return {
    status: check.status,
    label: reading.label,
    tone: reading.tone,
    budget:
      check.budget === null || check.budget === undefined
        ? 'not stated'
        : `${formatNumber(check.budget)} ${unitLabel(check.unit)}`.trim(),
    margin: hasMargin ? formatMargin(check.margin as number, check.unit) : 'no margin',
    hasMargin,
    note: check.note ?? '',
  }
}

/**
 * The budget a design row carries for one quantity, if any.
 *
 * Tolerates a row with no `budgets` map at all, which is what an older server
 * answers: absent and empty mean the same thing here, and neither is a verdict.
 *
 * @param row one design row
 * @param attribute the quantity's key, the same one `totals` is keyed by
 */
export function budgetOf(
  row: DesignRow | null | undefined,
  attribute: string,
): BudgetCheck | null {
  return row?.budgets?.[attribute] ?? null
}

/**
 * Every budget on one row, in the model's own attribute order.
 *
 * Ordered by `attributes` rather than by the map's own keys, so the budgets
 * read down the page in the same order as the tiles beside them. Rows with no
 * budget are simply absent - the list is as long as the part has figures.
 *
 * @param row one design row
 * @param attributes the payload's `attributes`, which fixes the order
 */
export function budgetsOn(
  row: DesignRow | null | undefined,
  attributes: readonly RollingAttribute[],
): { attribute: RollingAttribute; check: BudgetCheck; reading: BudgetReading }[] {
  const found: { attribute: RollingAttribute; check: BudgetCheck; reading: BudgetReading }[] = []
  for (const attribute of attributes) {
    const check = budgetOf(row, attribute.name)
    const reading = readBudget(check)
    if (check && reading) found.push({ attribute, check, reading })
  }
  return found
}

/**
 * The parts that state nothing, short enough to read.
 *
 * NAMED rather than counted, because "partial" on its own tells nobody what to
 * do about it - the list is the to-do. But BALROG states no mass anywhere, so
 * the honest list is twenty-four identifiers, and three tiles carrying it turn
 * the one useful sentence on the tile into a wall. The server already caps its
 * own budget notes at three and a count; this is the same cap for the same
 * reason, and the full list stays available to whatever renders it.
 *
 * @param unstated every part named as stating nothing
 * @param limit how many to name before counting the rest
 */
export function unstatedSummary(unstated: readonly string[], limit = 4): string {
  if (unstated.length === 0) return ''
  if (unstated.length <= limit) return unstated.join(', ')
  const named = unstated.slice(0, limit).join(', ')
  return `${named} and ${unstated.length - limit} more`
}

/**
 * A root and everything under it, as flat rows with their positions.
 *
 * INCLUDES THE ROOT. A budget on the vehicle is the ceiling every allocation
 * below is carved out of, and a list of "the budgets in this design" that
 * omitted it would be missing the only one that constrains the others.
 *
 * The rows are depth-first, so a subtree is the run of rows deeper than its
 * root - the same walk `descendantCount` does, and the same off-by-one to
 * avoid: stopping at `depth < root.depth` swallows every sibling that follows.
 *
 * @param items every design row, depth-first
 * @param index the root's position
 */
export function subtreeOf(
  items: DesignRow[],
  index: number,
): { index: number; row: DesignRow }[] {
  const root = items[index]
  if (!root) return []
  const found = [{ index, row: root }]
  for (let at = index + 1; at < items.length; at += 1) {
    if (items[at].depth <= root.depth) break
    found.push({ index: at, row: items[at] })
  }
  return found
}

/** One budget, with the part it is on and the quantity it is about. */
export interface AllocationRow {
  /** Unique in one table: the row's position, and the attribute. */
  key: string
  /** Where in the flat rows, so a caller can link through to the part. */
  index: number
  row: DesignRow
  attribute: RollingAttribute
  total: PartTotal | null
  check: BudgetCheck
  reading: BudgetReading
}

/**
 * Every budget in one root's subtree, part by part, quantity by quantity.
 *
 * A LONG TABLE RATHER THAN A WIDE ONE, and that follows from the data: budgets
 * are sparse - a few attributes on a handful of subsystems - so a column per
 * quantity would be mostly empty while the rows that do carry a figure are the
 * whole point. One row per budget puts them together and lets the verdicts be
 * read down a single column.
 *
 * Ordered by the tree, not by severity. Sorting the breaches to the top would
 * read better and would lose the thing a person is actually checking: whether
 * the allocations under one subsystem add up, which needs them adjacent.
 *
 * @param items every design row
 * @param index the root's position
 * @param attributes the payload's `attributes`, which fixes the column order
 */
export function allocationRows(
  items: DesignRow[],
  index: number,
  attributes: readonly RollingAttribute[],
): AllocationRow[] {
  const found: AllocationRow[] = []
  for (const { index: at, row } of subtreeOf(items, index)) {
    for (const { attribute, check, reading } of budgetsOn(row, attributes)) {
      found.push({
        key: `${at}:${attribute.name}`,
        index: at,
        row,
        attribute,
        total: row.totals?.[attribute.name] ?? null,
        check,
        reading,
      })
    }
  }
  return found
}

/**
 * How many of each verdict, for a one-line summary above the detail.
 *
 * Counted over the rows a person is actually looking at rather than over the
 * whole design, because the roots overlap: the same subsystem reached through
 * the vehicle and declared standalone is ONE budget breached, and counting both
 * reports two.
 */
export function countVerdicts(
  rows: readonly DesignRow[],
  attributes: readonly RollingAttribute[],
): Record<ComplianceStatus, number> {
  const tally: Record<ComplianceStatus, number> = {
    over: 0, within: 0, indeterminate: 0, not_comparable: 0, unbudgeted: 0,
  }
  for (const row of rows) {
    for (const { check } of budgetsOn(row, attributes)) {
      tally[check.status] = (tally[check.status] ?? 0) + 1
    }
  }
  return tally
}
